import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: "px-6 py-2",
  "data-cy": "agent-access-page"
}
const _hoisted_2 = { class: "py-2" }
const _hoisted_3 = { class: "font-weight-medium" }
const _hoisted_4 = { class: "text-caption text-grey-darken-1" }
const _hoisted_5 = { class: "mt-1" }
const _hoisted_6 = {
  key: 0,
  class: "text-caption text-grey-darken-1 mt-1"
}
const _hoisted_7 = ["data-cy"]
const _hoisted_8 = {
  key: 0,
  class: "text-grey"
}
const _hoisted_9 = { key: 1 }
const _hoisted_10 = {
  key: 0,
  class: "text-grey"
}
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { class: "text-right whitespace-nowrap" }
const _hoisted_13 = {
  key: 0,
  class: "text-body-2 text-grey-darken-1 py-8 text-center",
  "data-cy": "no-connections"
}
const _hoisted_14 = { class: "py-1" }
const _hoisted_15 = { class: "text-caption text-grey-darken-1" }
const _hoisted_16 = ["data-cy", "onClick"]
const _hoisted_17 = { key: 0 }
const _hoisted_18 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_19 = { class: "text-right" }

import { computed, onMounted, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import LlTabs from '@/components/LlTabs.vue';
import MainHeader from '@/components/MainHeader.vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getShortDateString } from '@/services/utils';
import ApproveAgentDialog from './components/ApproveAgentDialog.vue';
import ConnectionPermissionsDialog from './components/ConnectionPermissionsDialog.vue';
import { useAgentAccessStore } from './stores/useAgentAccessStore';
import type {
  AgentAccessAgent,
  AgentAccessConnection,
  AgentAccessPermission,
  AgentStatus,
} from './models/agentAccessTypes';


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentAccess',
  setup(__props) {

const STATUS_LABELS: Record<AgentStatus, string> = {
  none: 'Not approved',
  approved: 'Approved',
  withdrawn: 'Withdrawn',
  requested: 'Requested',
};

const STATUS_TONES: Record<AgentStatus, 'green' | 'red' | 'amber' | 'grey'> = {
  none: 'grey',
  approved: 'green',
  withdrawn: 'red',
  requested: 'amber',
};

const agentHeaders = [
  { title: 'Agent', key: 'name', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Permissions', key: 'permissions', sortable: false },
  { title: 'Connections', key: 'connections', sortable: false },
  { title: '', key: 'actions', sortable: false },
];

const connectionHeaders = [
  { title: 'User', key: 'userName', sortable: false },
  { title: 'Agent', key: 'agentName', sortable: false },
  { title: 'Permissions', key: 'grantedScopes', sortable: false },
  { title: 'Last Used', key: 'lastUsedAtUtc', sortable: false },
  { title: '', key: 'actions', sortable: false },
];

const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const agentAccessStore = useAgentAccessStore();
const { agents, connections, permissions, loading, saving } = storeToRefs(agentAccessStore);

const agentTabs = computed(() => [
  { value: 'agents', label: 'Agents', dataCy: 'tab-agents' },
  {
    value: 'connections',
    label: `Connections (${connections.value.length})`,
    dataCy: 'tab-connections',
  },
]);
const { handleError, handleSuccess } = useSnackbarHandler();

const withdrawDialog = ref();
const restoreDialog = ref();
const revokeDialog = ref();

const uiState = reactive({
  tab: 'agents',
  showApprove: false,
  showConnection: false,
  selectedAgent: null as AgentAccessAgent | null,
  selectedConnection: null as AgentAccessConnection | null,
});

// access.setup is already true for a legacy `admin` role — the API folds both into it — so this is
// Account.IsAdmin(), not a narrower reading of it.
const isAdmin = computed(() => !!me.value?.access?.setup);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Counted the way the permission picker ticks its boxes: wildcards unfold into what they cover and are
// not counted themselves, staying connected is always in, and no cap (null) is everything except the
// permissions that have to be named — so the number matches what an administrator sees on opening it.
const isWildcard = (scope: string) => scope.endsWith(':*');

const coversPermission = (scopes: string[] | null, p: AgentAccessPermission) => {
  if (p.scope === 'offline_access') return true;
  if (!scopes) return !p.requiresExplicitGrant;
  if (scopes.includes(p.scope)) return true;
  if (p.requiresExplicitGrant) return false;
  return scopes.includes('write:*') || (!p.isWrite && scopes.includes('read:*'));
};

const capSummary = (agent: AgentAccessAgent) => {
  const detailed = permissions.value.filter((p) => !isWildcard(p.scope));
  const withheld = detailed.filter((p) => !coversPermission(agent.allowedScopes, p));
  if (!withheld.length) return 'All permissions';
  if (withheld.every((p) => p.requiresExplicitGrant)) return 'All except sending email';
  return `${detailed.length - withheld.length} of ${detailed.length}`;
};

// Shown for an approved or withdrawn agent too: someone trying an agent the firm already withdrew is
// the reason to look at it again.
const triedToConnect = (agent: AgentAccessAgent) => {
  if (!agent.requestedAtUtc) return '';
  const who = agent.requestedBy ?? 'Someone';
  return `${who} tried to connect, ${getShortDateString(agent.requestedAtUtc)}`;
};

const decidedBy = (agent: AgentAccessAgent) => {
  if (agent.status === 'withdrawn' && agent.withdrawnBy) {
    return `${agent.withdrawnBy}, ${getShortDateString(agent.withdrawnAtUtc)}`;
  }
  if (agent.status === 'approved' && agent.approvedBy) {
    return `${agent.approvedBy}, ${getShortDateString(agent.approvedAtUtc)}`;
  }
  return '';
};

const withdrawMessage = computed(() => {
  const agent = uiState.selectedAgent;
  if (!agent) return '';
  const count = agent.activeConnectionCount;
  const ends = count
    ? `This ends ${plural(count, 'active connection')} straight away. Those agents lose access immediately, not just at next sign-in.`
    : 'There are no active connections to end.';
  return `${ends}\n\nNobody at your firm will be able to connect ${agent.name} until you approve it again.\n\nReversible — restoring the approval brings those connections back with the permissions people already gave, without asking them again.`;
});

const restoreMessage = computed(() => {
  const agent = uiState.selectedAgent;
  if (!agent) return '';
  return `People at your firm will be able to connect ${agent.name} again, and any connections withdrawn earlier will start working again with the permissions already approved — nobody is asked to consent a second time.\n\nRecorded against your name on the firm's audit trail.`;
});

const revokeMessage = computed(() => {
  const connection = uiState.selectedConnection;
  if (!connection) return '';
  const first = connection.userName.split(' ')[0];
  return `${connection.userName} (${connection.userEmail}) loses access through ${connection.agentName} straight away.\n\nThis one is not reversible. ${first} can reconnect from inside the agent, which creates a new connection and a fresh consent.`;
});

const load = async () => {
  try {
    await agentAccessStore.load();
  } catch (error) {
    handleError(error);
  }
};

onMounted(async () => {
  try {
    await meStore.getMe();
  } catch (error) {
    handleError(error);
    return;
  }
  if (isAdmin.value) {
    await load();
  }
});

const openApprove = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  uiState.showApprove = true;
};

const askWithdraw = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  withdrawDialog.value?.open();
};

const askRestore = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  restoreDialog.value?.open();
};

const askRevoke = (connection: AgentAccessConnection) => {
  uiState.selectedConnection = connection;
  revokeDialog.value?.open();
};

const inspect = (connection: AgentAccessConnection) => {
  uiState.selectedConnection = connection;
  uiState.showConnection = true;
};

const approve = async ({ clientId, allowedScopes }: { clientId: string; allowedScopes: string[] | null }) => {
  const editing = uiState.selectedAgent?.status === 'approved';
  try {
    await agentAccessStore.approve(clientId, allowedScopes);
    uiState.showApprove = false;
    handleSuccess(editing ? 'Permissions updated' : 'Agent approved');
  } catch (error) {
    handleError(error);
  }
};

const withdraw = async () => {
  if (!uiState.selectedAgent) return;
  try {
    await agentAccessStore.withdraw(uiState.selectedAgent.clientId);
    handleSuccess('Agent withdrawn');
  } catch (error) {
    handleError(error);
  }
};

const restore = async () => {
  if (!uiState.selectedAgent) return;
  try {
    await agentAccessStore.restore(uiState.selectedAgent.clientId);
    handleSuccess('Agent restored');
  } catch (error) {
    handleError(error);
  }
};

const revoke = async () => {
  if (!uiState.selectedConnection) return;
  try {
    await agentAccessStore.revokeConnection(uiState.selectedConnection.id);
    handleSuccess('Connection revoked');
  } catch (error) {
    handleError(error);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MainHeader, { content: "AI Agent Access" }),
    (!isAdmin.value)
      ? (_openBlock(), _createBlock(_component_v_alert, {
          key: 0,
          type: "info",
          variant: "tonal",
          class: "mt-4",
          "data-cy": "agent-access-denied"
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" Only a firm administrator can change agent access. ", -1)
          ]))]),
          _: 1
        }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-body-2 text-grey-darken-2 mb-4" }, " Agents can only be connected by people at your firm after you approve them here. ", -1)),
          _createVNode(LlTabs, {
            modelValue: uiState.tab,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((uiState.tab) = $event)),
            items: agentTabs.value,
            class: "mb-4"
          }, null, 8, ["modelValue", "items"]),
          (_unref(loading))
            ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                key: 0,
                type: "table-row-divider@4",
                class: "w-full",
                elevation: "0"
              }))
            : (_openBlock(), _createBlock(_component_v_window, {
                key: 1,
                modelValue: uiState.tab,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((uiState.tab) = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_window_item, { value: "agents" }, {
                    default: _withCtx(() => [
                      _createVNode(LlDataTable, {
                        headers: agentHeaders,
                        items: _unref(agents),
                        "items-length": _unref(agents).length,
                        "item-value": "clientId",
                        "must-sort": false,
                        "data-cy": "agents-table"
                      }, {
                        "item.name": _withCtx(({ item }) => [
                          _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, _toDisplayString(item.name), 1),
                            _createElementVNode("div", _hoisted_4, _toDisplayString(item.origin), 1),
                            _createElementVNode("div", _hoisted_5, [
                              _createVNode(LlStatusBadge, {
                                tone: item.isVerifiedPublisher ? 'green' : 'amber'
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(item.isVerifiedPublisher ? 'Verified' : 'Unverified'), 1)
                                ]),
                                _: 2
                              }, 1032, ["tone"])
                            ])
                          ])
                        ]),
                        "item.status": _withCtx(({ item }) => [
                          _createVNode(LlStatusBadge, {
                            tone: STATUS_TONES[item.status],
                            "data-cy": `status-${item.clientId}`
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(STATUS_LABELS[item.status]), 1)
                            ]),
                            _: 2
                          }, 1032, ["tone", "data-cy"]),
                          (decidedBy(item))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(decidedBy(item)), 1))
                            : _createCommentVNode("", true),
                          (triedToConnect(item))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: "text-caption text-grey-darken-1 mt-1",
                                "data-cy": `requested-${item.clientId}`
                              }, _toDisplayString(triedToConnect(item)), 9, _hoisted_7))
                            : _createCommentVNode("", true)
                        ]),
                        "item.permissions": _withCtx(({ item }) => [
                          (item.status !== 'approved')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_8, "—"))
                            : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(capSummary(item)), 1))
                        ]),
                        "item.connections": _withCtx(({ item }) => [
                          (item.status !== 'approved')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_10, "—"))
                            : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(item.activeConnectionCount), 1))
                        ]),
                        "item.actions": _withCtx(({ item }) => [
                          _createElementVNode("div", _hoisted_12, [
                            (item.status === 'approved')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createVNode(LlSecondaryButton, {
                                    size: "small",
                                    "data-cy": `permissions-${item.clientId}`,
                                    onClick: ($event: any) => (openApprove(item))
                                  }, {
                                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                      _createTextVNode(" Permissions ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["data-cy", "onClick"]),
                                  _createVNode(LlSecondaryButton, {
                                    size: "small",
                                    color: "error",
                                    class: "ml-2",
                                    "data-cy": `withdraw-${item.clientId}`,
                                    onClick: ($event: any) => (askWithdraw(item))
                                  }, {
                                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                      _createTextVNode(" Withdraw ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["data-cy", "onClick"])
                                ], 64))
                              : (item.status === 'withdrawn')
                                ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                    key: 1,
                                    size: "small",
                                    "data-cy": `restore-${item.clientId}`,
                                    onClick: ($event: any) => (askRestore(item))
                                  }, {
                                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                      _createTextVNode(" Restore ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["data-cy", "onClick"]))
                                : (_openBlock(), _createBlock(LlSecondaryButton, {
                                    key: 2,
                                    size: "small",
                                    color: "primary",
                                    "data-cy": `approve-${item.clientId}`,
                                    onClick: ($event: any) => (openApprove(item))
                                  }, {
                                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                      _createTextVNode(" Approve ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["data-cy", "onClick"]))
                          ])
                        ]),
                        _: 1
                      }, 8, ["items", "items-length"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_window_item, { value: "connections" }, {
                    default: _withCtx(() => [
                      (!_unref(connections).length)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_13, " No live connections. People connect an agent from within the agent itself, once you have approved it. "))
                        : (_openBlock(), _createBlock(LlDataTable, {
                            key: 1,
                            headers: connectionHeaders,
                            items: _unref(connections),
                            "items-length": _unref(connections).length,
                            "item-value": "id",
                            "must-sort": false,
                            "data-cy": "connections-table"
                          }, {
                            "item.userName": _withCtx(({ item }) => [
                              _createElementVNode("div", _hoisted_14, [
                                _createElementVNode("div", null, _toDisplayString(item.userName), 1),
                                _createElementVNode("div", _hoisted_15, _toDisplayString(item.userEmail), 1)
                              ])
                            ]),
                            "item.grantedScopes": _withCtx(({ item }) => [
                              _createElementVNode("a", {
                                href: "#",
                                class: "text-blue-darken-2",
                                "data-cy": `inspect-${item.id}`,
                                onClick: _withModifiers(($event: any) => (inspect(item)), ["prevent"])
                              }, _toDisplayString(item.grantedScopes.length), 9, _hoisted_16)
                            ]),
                            "item.lastUsedAtUtc": _withCtx(({ item }) => [
                              (item.lastUsedAtUtc)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_unref(getShortDateString)(item.lastUsedAtUtc)), 1))
                                : (_openBlock(), _createElementBlock("span", _hoisted_18, "Never"))
                            ]),
                            "item.actions": _withCtx(({ item }) => [
                              _createElementVNode("div", _hoisted_19, [
                                _createVNode(LlSecondaryButton, {
                                  size: "small",
                                  color: "error",
                                  "data-cy": `revoke-${item.id}`,
                                  onClick: ($event: any) => (askRevoke(item))
                                }, {
                                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                    _createTextVNode(" Revoke ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["data-cy", "onClick"])
                              ])
                            ]),
                            _: 1
                          }, 8, ["items", "items-length"]))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]))
        ], 64)),
    _createVNode(ApproveAgentDialog, {
      modelValue: uiState.showApprove,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((uiState.showApprove) = $event)),
      agent: uiState.selectedAgent,
      permissions: _unref(permissions),
      saving: _unref(saving),
      onApprove: approve
    }, null, 8, ["modelValue", "agent", "permissions", "saving"]),
    _createVNode(ConnectionPermissionsDialog, {
      modelValue: uiState.showConnection,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((uiState.showConnection) = $event)),
      connection: uiState.selectedConnection,
      permissions: _unref(permissions)
    }, null, 8, ["modelValue", "connection", "permissions"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "withdrawDialog",
      ref: withdrawDialog,
      title: "Withdraw this agent?",
      message: withdrawMessage.value,
      "confirm-text": "Withdraw",
      "confirm-color": "error",
      "data-cy": "withdraw-dialog",
      "preserve-newlines": "",
      onConfirm: withdraw
    }, null, 8, ["message"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "restoreDialog",
      ref: restoreDialog,
      title: "Restore this agent?",
      message: restoreMessage.value,
      "confirm-text": "Restore",
      "confirm-color": "primary",
      "data-cy": "restore-dialog",
      "preserve-newlines": "",
      onConfirm: restore
    }, null, 8, ["message"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "revokeDialog",
      ref: revokeDialog,
      title: "Revoke this connection?",
      message: revokeMessage.value,
      "confirm-text": "Revoke",
      "confirm-color": "error",
      "data-cy": "revoke-dialog",
      "preserve-newlines": "",
      onConfirm: revoke
    }, null, 8, ["message"])
  ]))
}
}

})